import { Payment, PaymentType } from '@bulthaup/api/db';
import { ActionIcon, Card, Table, Text, Title } from '@mantine/core';
import { modals, openConfirmModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconTrash } from '@tabler/icons-react';

import { trpc } from '../../../../../../services/trpc';
import { formatAmount, formatDate } from '../../../../../../utils/format';
import { label } from '../../../../../../utils/label';
import { PaymentForm } from './-payment-form';

export const PaymentList = ({
  type,
  payments,
}: {
  type: PaymentType;
  payments: Payment[];
}) => {
  const updatePaymentMutation = trpc.projects.updatePayment.useMutation();
  const deletePaymentMutation = trpc.projects.deletePayment.useMutation();

  const openEditModal = (payment: Payment) => {
    modals.open({
      title: 'Modifier le paiement',
      children: (
        <PaymentForm
          initialValues={{
            amount: payment.amount ? Number(payment.amount) : null,
            date: new Date(payment.date),
            type: payment.type,
          }}
          onSubmit={async values => {
            await updatePaymentMutation.mutateAsync({
              id: payment.id,
              amount: Number(values.amount),
              type: values.type,
              date: values.date,
            });
            modals.closeAll();
            notifications.show({
              title: 'Paiement mis à jour avec succès',
              message: '',
              color: 'green',
            });
          }}
        />
      ),
    });
  };

  const deletePayment = async (id: string) => {
    openConfirmModal({
      title: 'Supprimer le paiement',
      children:
        'Êtes-vous sûr de vouloir supprimer ce paiement ? Cette action est irréversible.',
      labels: { confirm: 'Supprimer', cancel: 'Annuler' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deletePaymentMutation.mutateAsync({ id });
        notifications.show({
          title: 'Paiement supprimé avec succès',
          message: '',
          color: 'green',
        });
      },
    });
  };

  return (
    <Card withBorder p="md" bg="gray.0">
      <Title order={5} mb="md" c="dimmed">
        {label(type)}
      </Title>
      {payments.length === 0 ? (
        <Text size="sm" c="dimmed">
          Aucun paiement
        </Text>
      ) : (
        <Table>
          <Table.Tbody>
            {payments.map((payment, index) => (
              <Table.Tr key={payment.id}>
                <Table.Td>
                  <Text>
                    {index + 1}
                    {index === 0 ? 'er' : 'ème'}
                  </Text>
                </Table.Td>
                <Table.Td>{formatDate(payment.date)}</Table.Td>
                <Table.Td fw={700} style={{ textAlign: 'right' }}>
                  {formatAmount(payment.amount)}
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    onClick={() => openEditModal(payment)}
                    aria-label="Modifier le paiement"
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    onClick={() => deletePayment(payment.id)}
                    aria-label="Supprimer le paiement"
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
};
